import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/shadcn/dialog';
import { Button } from '../../ui/shadcn/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import monitoringService, { DrillDownItem } from '../../../services/monitoring';
import { colors, spacing, borderRadius, typography } from '../../../styles/design-tokens';

interface Props {
  open: boolean;
  onClose: () => void;
  items: Array<DrillDownItem & { menu: string; menu_label: string }>;
  onSuccess: () => void;
}

const BulkDeleteDialog: React.FC<Props> = ({ open, onClose, items, onSuccess }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const byMenu = items.reduce((acc, it) => {
    if (!acc[it.menu]) acc[it.menu] = { label: it.menu_label, ids: [] };
    acc[it.menu].ids.push(it.id);
    return acc;
  }, {} as Record<string, { label: string; ids: string[] }>);

  const handleConfirm = async () => {
    setDeleting(true);
    setError('');
    try {
      const payload: Record<string, string[]> = {};
      Object.entries(byMenu).forEach(([menu, info]) => { payload[menu] = info.ids; });
      const result = await monitoringService.bulkDelete(payload);
      if (result.failed === 0) {
        toast.success(`${result.success}건 삭제 완료`);
      } else {
        toast.error(`${result.success}건 성공, ${result.failed}건 실패`);
      }
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.message || '삭제 실패');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md sm:max-w-md">
        <DialogHeader>
          <DialogTitle style={{
            display: 'flex', alignItems: 'center', gap: spacing.sm,
            fontSize: typography.heading.h3.fontSize, color: colors.destructive,
          }}>
            <AlertTriangle size={22} />
            선택한 {items.length}건을 삭제하시겠습니까?
          </DialogTitle>
          <DialogDescription>
            이 작업은 되돌릴 수 없습니다. 관련 자식 데이터가 함께 삭제될 수 있습니다.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: spacing.sm,
            padding: spacing.md, background: 'var(--ws-red-bg)',
            border: `1px solid ${colors.destructive}`, color: colors.destructive,
            borderRadius: borderRadius.md, fontSize: 13,
          }}>
            <AlertTriangle size={16} />{error}
          </div>
        )}

        <div style={{
          padding: spacing.md,
          background: '#fef7f7', border: `1px solid #fecaca`,
          borderRadius: borderRadius.md,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: colors.text.primary, marginBottom: spacing.xs }}>
            메뉴별 삭제 대상
          </div>
          {Object.entries(byMenu).map(([menu, info]) => (
            <div key={menu} style={{ fontSize: 13, color: colors.text.secondary, padding: '2px 0' }}>
              · {info.label} <strong>{info.ids.length}건</strong>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>취소</Button>
          <Button
            onClick={handleConfirm}
            disabled={deleting}
            style={{ background: colors.destructive, color: 'var(--ws-surface)' }}
          >
            {deleting ? (<><Loader2 size={14} className="animate-spin" style={{ marginRight: 6 }} />삭제 중...</>) : `${items.length}건 삭제`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkDeleteDialog;
